import type { ReactNode } from 'react'
import type { ProductVariant } from '@/types'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react'

import { setScrollLocked } from '@/lib/scroll'
import { productVariants } from '@/constants/product'

const STORAGE_KEY = 'essentia.bag'

export type BagLine = {
    variantId: string
    quantity: number
}

type BagContextValue = {
    lines: BagLine[]
    count: number
    subtotal: number
    isOpen: boolean
    openBag: () => void
    closeBag: () => void
    addLine: (variantId: string, quantity: number) => void
    setQuantity: (variantId: string, quantity: number) => void
    removeLine: (variantId: string) => void
    variantFor: (variantId: string) => ProductVariant | undefined
}

const BagContext = createContext<BagContextValue | null>(null)

const isBagLine = (value: unknown): value is BagLine => {
    if (typeof value !== 'object' || value === null) return false

    const line = value as Record<string, unknown>

    return typeof line.variantId === 'string' && typeof line.quantity === 'number'
}

export const BagProvider = ({ children }: { children: ReactNode }): ReactNode => {
    const [lines, setLines] = useState<BagLine[]>([])
    const [isOpen, setIsOpen] = useState(false)
    const [hydrated, setHydrated] = useState(false)

    // Read persisted state after mount only, so the server and first client render agree.
    useEffect(() => {
        try {
            const saved = window.localStorage.getItem(STORAGE_KEY)

            if (saved) {
                const parsed: unknown = JSON.parse(saved)

                if (Array.isArray(parsed)) {
                    setLines(parsed.filter(isBagLine))
                }
            }
        } catch {
            // A corrupt or unavailable store just means we start with an empty bag.
        }

        setHydrated(true)
    }, [])

    useEffect(() => {
        if (!hydrated) return

        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(lines))
        } catch {
            // Storage can be full or blocked; the bag still works for this session.
        }
    }, [lines, hydrated])

    // Lock the page behind the drawer while it is open.
    useEffect(() => {
        if (!isOpen) return

        const previous = document.body.style.overflow

        document.body.style.overflow = 'hidden'
        setScrollLocked(true)

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setIsOpen(false)
        }

        window.addEventListener('keydown', onKey)

        return () => {
            document.body.style.overflow = previous
            setScrollLocked(false)
            window.removeEventListener('keydown', onKey)
        }
    }, [isOpen])

    const variantFor = useCallback(
        (variantId: string) => productVariants.find((variant) => variant.id === variantId),
        []
    )

    const addLine = useCallback((variantId: string, quantity: number) => {
        setLines((current) => {
            const existing = current.find((line) => line.variantId === variantId)

            if (existing) {
                return current.map((line) =>
                    line.variantId === variantId
                        ? { ...line, quantity: Math.min(line.quantity + quantity, 20) }
                        : line
                )
            }

            return [...current, { variantId, quantity: Math.min(quantity, 20) }]
        })

        setIsOpen(true)
    }, [])

    const setQuantity = useCallback((variantId: string, quantity: number) => {
        setLines((current) => {
            if (quantity < 1) {
                return current.filter((line) => line.variantId !== variantId)
            }

            return current.map((line) =>
                line.variantId === variantId
                    ? { ...line, quantity: Math.min(quantity, 20) }
                    : line
            )
        })
    }, [])

    const removeLine = useCallback((variantId: string) => {
        setLines((current) => current.filter((line) => line.variantId !== variantId))
    }, [])

    const value = useMemo<BagContextValue>(() => {
        const count = lines.reduce((total, line) => total + line.quantity, 0)

        const subtotal = lines.reduce((total, line) => {
            const variant = productVariants.find((entry) => entry.id === line.variantId)
            return total + (variant ? variant.price * line.quantity : 0)
        }, 0)

        return {
            lines,
            count,
            subtotal,
            isOpen,
            openBag: () => setIsOpen(true),
            closeBag: () => setIsOpen(false),
            addLine,
            setQuantity,
            removeLine,
            variantFor
        }
    }, [lines, isOpen, addLine, setQuantity, removeLine, variantFor])

    return (
        <BagContext.Provider value={value}>
            {children}
        </BagContext.Provider>
    )
}

export const useBag = (): BagContextValue => {
    const context = useContext(BagContext)

    if (!context) {
        throw new Error('useBag must be used inside a BagProvider')
    }

    return context
}
